import { startOfWeek, addDays, format, parse, isSaturday, subDays, addWeeks } from 'date-fns';

/**
 * Get the previous Saturday from a given date
 * Week starts on Saturday in this application
 */
export const getPreviousSaturday = (date: Date): Date => {
  if (isSaturday(date)) {
    return date;
  }
  // Start of week (Sunday) then subtract 1 day to get Saturday
  return subDays(startOfWeek(date), 1);
};

/**
 * Format date as YYYYMMDD string for API
 */
export const formatDateForAPI = (date: Date): string => {
  return format(date, 'yyyyMMdd');
};

/**
 * Parse YYYYMMDD string from API to Date
 */
export const parseDateFromAPI = (dateStr: string): Date => {
  return parse(dateStr, 'yyyyMMdd', new Date());
};

/**
 * Format date for display (MM/DD/YYYY)
 */
export const formatDateForDisplay = (date: Date): string => {
  return format(date, 'MM/dd/yyyy');
};

/**
 * Get array of dates for a week starting from Saturday
 */
export const getWeekDates = (startDate: Date): Date[] => {
  const saturday = getPreviousSaturday(startDate);
  return Array.from({ length: 7 }, (_, i) => addDays(saturday, i));
};

/**
 * Get week label for display (e.g., "12/23/2023 - 12/29/2023")
 */
export const getWeekLabel = (startDate: Date): string => {
  const saturday = getPreviousSaturday(startDate);
  const friday = addDays(saturday, 6);
  return `${formatDateForDisplay(saturday)} - ${formatDateForDisplay(friday)}`;
};

/**
 * Navigate to next or previous week
 */
export const navigateWeek = (currentDate: Date, direction: 1 | -1): Date => {
  return addWeeks(currentDate, direction);
};

/**
 * Check if a week contains the year-end (December 31)
 * Returns information about the year transition
 */
export const weekContainsYearEnd = (weekStart: Date): {
  containsYearEnd: boolean;
  oldYear: number | null;
  newYear: number | null;
} => {
  const saturday = getPreviousSaturday(weekStart);

  // Check if this is the last week of December
  if (saturday.getMonth() === 11 && saturday.getDate() > 25) {
    return {
      containsYearEnd: true,
      oldYear: saturday.getFullYear(),
      newYear: saturday.getFullYear() + 1,
    };
  }

  return {
    containsYearEnd: false,
    oldYear: null,
    newYear: null,
  };
};

/**
 * Convert HTML date input format (YYYY-MM-DD) to YYYYMMDD
 */
export const htmlDateToAPI = (htmlDate: string): string => {
  if (!htmlDate) return '';
  return htmlDate.replace(/-/g, '');
};

/**
 * Convert YYYYMMDD to HTML date input format (YYYY-MM-DD)
 */
export const apiDateToHTML = (apiDate: string): string => {
  if (!apiDate || apiDate === '0') return '';
  if (apiDate.length !== 8) return '';

  return `${apiDate.substring(0, 4)}-${apiDate.substring(4, 6)}-${apiDate.substring(6, 8)}`;
};

/**
 * Day names for the week (starting with Saturday)
 */
export const WEEK_DAYS = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'] as const;

/**
 * Get day name and date for column headers
 */
export const getDayHeader = (date: Date, dayIndex: number): string => {
  return `${WEEK_DAYS[dayIndex]} ${format(date, 'MM/dd')}`;
};
